import { MarketStatus } from '@velocity-exchange/sdk';
import { ENUM_UTILS } from '@velocity-exchange/common';
import Env from 'src/environmentVariables/EnvironmentVariables';

export type DelistingBatch = {
	marketIndices: number[];
	timestamp: number;
};

export function parseDelistingConfig(
	raw: string | undefined
): DelistingBatch[] {
	if (!raw) return [];
	try {
		const parsed = JSON.parse(raw) as unknown;
		if (!Array.isArray(parsed)) return [];
		return parsed
			.filter(
				(item): item is { marketIndices: number[]; timestamp: number } =>
					item != null &&
					typeof item === 'object' &&
					Array.isArray((item as { marketIndices?: unknown }).marketIndices) &&
					typeof (item as { timestamp?: unknown }).timestamp === 'number'
			)
			.map((item) => ({
				marketIndices: item.marketIndices.filter(
					(n): n is number => typeof n === 'number'
				),
				timestamp: item.timestamp,
			}));
	} catch {
		return [];
	}
}

export function getNextDelistingTs(
	config: DelistingBatch[],
	fallbackTs: number
): number {
	if (config.length === 0) return fallbackTs;
	const futureTimestamps = config
		.map((b) => b.timestamp)
		.filter((ts) => ts > Date.now());
	return futureTimestamps.length > 0
		? Math.min(...futureTimestamps)
		: Math.min(...config.map((b) => b.timestamp));
}

/** Returns true if market is in reduce-only based on chain status. */
export const isMarketReduceOnly = (marketStatus?: MarketStatus): boolean =>
	marketStatus !== undefined &&
	ENUM_UTILS.match(marketStatus, MarketStatus.REDUCE_ONLY);

/** Returns true if market is in delisting config and the delisting timestamp is still in the future. */
export const marketToBeDelisted = (marketIndex: number | undefined) => {
	if (marketIndex === undefined) return false;
	return Env.delistingConfig.some(
		(batch) =>
			batch.marketIndices.includes(marketIndex) && Date.now() < batch.timestamp
	);
};

/** Returns true if the next delisting timestamp is in the future (don't show delisting UI when past). */
export const isDelistingTimestampInFuture = () =>
	Date.now() < Env.nextDelistingTs;

const formatDelistingDate = (timestamp: number): string => {
	const date = new Date(timestamp);
	return date.toLocaleString('en-US', {
		month: 'short',
		day: 'numeric',
		year: 'numeric',
		hour: '2-digit',
		minute: '2-digit',
		timeZone: 'UTC',
		timeZoneName: 'short',
	});
};

export const getDelistingTextOpenPosition = () => {
	const delistingDateString = formatDelistingDate(Env.nextDelistingTs);
	return `This market will be delisted on ${delistingDateString}. Your position is now in reduce-only mode. After delisting, any remaining balance will be automatically transferred to your Velocity account.`;
};

export const getDelistingTextNoPosition = () => {
	const delistingDateString = formatDelistingDate(Env.nextDelistingTs);
	return `This market will be delisted on ${delistingDateString}. Any new orders must be in reduce-only mode. After delisting, any remaining balance will be automatically transferred to your Velocity account.`;
};
